import type { ArabicFontFamily } from '../preferences/types'

/**
 * Single source of truth for the Arabic font stacks. VerseDisplay, BookView,
 * and NowPlayingVerse previously carried private copies that drifted (the
 * playlist's `simple` mapped to an Amiri stack while the Reader's mapped to
 * Arial) — one map ends that class of divergence.
 */
export const ARABIC_FONT_FAMILIES: Record<ArabicFontFamily, string> = {
  kfgqpc: '"KFGQPC Uthmanic Script HAFS", "Amiri Quran", "Traditional Arabic", serif',
  uthmani: '"Amiri Quran", "Traditional Arabic", serif',
  indopak: '"Noto Nastaliq Urdu", "Traditional Arabic", serif',
  simple: '"Arial", "Helvetica", sans-serif',
}

export function arabicFontFamily(font: ArabicFontFamily): string {
  return ARABIC_FONT_FAMILIES[font]
}

/** Named breakpoint tier for `arabicVerseFontSize`, matching the three
 *  tiers VerseDisplay's `sx` breakpoint object resolves to (MUI's default
 *  `sm`/`md` thresholds: 600px / 900px). */
export type ArabicFontSizeTier = 'xs' | 'sm' | 'md'

/**
 * Mirrors VerseDisplay's responsive Arabic font-size formula exactly:
 * mobile (`xs`) renders at 70% of the preferred size (floor 32px), tablet
 * (`sm`) at 85% (floor 36px), desktop (`md`) at the raw preferred size.
 * VerseDisplay applies this as an MUI `sx` breakpoint object (real CSS
 * media queries) directly on a `Box`. Callers that render plain HTML
 * instead of an MUI component — e.g. SurahReaderPage's crawlable SEO
 * body, which intentionally keeps the ayah markup free of MUI/emotion
 * class names — resolve the applicable tier once (typically via
 * `useMediaQuery`) and apply the single resulting pixel value as an
 * inline style, so both surfaces size Arabic text identically for the
 * same `prefs.fontSize` without duplicating VerseDisplay's numbers.
 */
export function arabicVerseFontSize(fontSize: number, tier: ArabicFontSizeTier): number {
  switch (tier) {
    case 'md':
      return fontSize
    case 'sm':
      return Math.max(36, Math.round(fontSize * 0.85))
    case 'xs':
      return Math.max(32, Math.round(fontSize * 0.7))
  }
}
